import React from 'react'
import { AppIcon, createStyles } from '@codeleap/styles'
import { AvatarInfo, Checkbox, Icon, Touchable, View } from '@/components'
import { Profile } from '@packages/types'
import { Medias } from '@/config/packages'

type User = {
  id: string
  icon?: AppIcon
  name: string
  image?: string
  avatar?: string
  role?: string
}

export type UserItemProps = {
  user: User
  isLastItem?: boolean
  isSelected?: boolean
  HeaderComponent?: React.ReactNode
  blockInteraction?: boolean
  currentProfile?: Profile
  onPress?: (user: User) => void
  onSelect?: (user: User) => void
  selectable?: boolean
  usersSelected?: User['id'][]
  roleShown?: boolean
  letterSeparatorShown?: boolean
  noInteractableUsers?: string[]
}

export function UserItem({ user, currentProfile, HeaderComponent, isLastItem, isSelected, blockInteraction, roleShown, ...props }: UserItemProps) {
  const isMe = currentProfile?.id === user.id
  const userName = isMe ? `${user.name} (You)` : user.name
  const { src: avatar } = Medias.useResolvedUrl(user.image || user.avatar)
  const roleTestId = roleShown && user.role
    ? `role:${user.role.toLowerCase()}`
    : undefined

  return (
    <View style={['column']}>
      {HeaderComponent}
      <Touchable
        debugName='Chat'
        onPress={blockInteraction ? null : (props?.onPress ? () => props?.onPress?.(user) : null)}
        style={[styles.itemWrapper, isLastItem && { borderBottomWidth: 0 }]}
      >
        <View style={['row', 'fullWidth', 'alignCenter', 'gap:1']}>
          <AvatarInfo
            title={userName}
            titleTestId={isMe ? 'useritem:currentuserbadge' : undefined}
            avatar={avatar}
            description={roleShown ? user.role : null}
            descriptionTestId={roleTestId}
            style={['flex', 'mid']}
          />

          {user.icon && !blockInteraction && (
            <Icon name={'chevron-right'} style={styles.icon} />
          )}

          {props?.selectable && (
            <Checkbox
              value={isSelected}
              style={['marginLeft:auto', 'circle', { pointerEvents: 'none' }]}
              onValueChange={() => props?.onSelect?.(user)}
            />
          )}
        </View>
      </Touchable>
    </View>
  )
}

const styles = createStyles(theme => ({
  itemWrapper: {
    ...theme.spacing.paddingHorizontal(3),
    ...theme.spacing.paddingVertical(1),
    ...theme.border({ color: 'neutralSolid300', width: 1, directions: ['bottom'] }),

    [theme.media.down('mobile')]: {
      ...theme.spacing.paddingHorizontal(2),
    },
  },
  icon: {
    marginLeft: 'auto',
    color: theme.baseColors.neutralSolid500,
    ...theme.sized(3),
  },
}))
